import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import "../styles/testimonials.css";
import Navbar from "../components/layout/Navbar";
import SeoHead from "../components/SeoHead";

// ── TESTIMONIALS DATA ─────────────────────────────
// `category` is used for filtering — keep values consistent
// with the FILTERS array below.
const TESTIMONIALS = [
  {
    name: "Ramesh K.",
    loc: "Hyderabad",
    text: "After a severe crush injury at work, Dr. Suneel performed emergency reconstructive surgery. Within months I regained complete hand function. His skill is extraordinary.",
    stars: 5,
    initials: "RK",
    tag: "Hand Reconstruction",
    category: "Hand",
    featured: true,
  },
  {
    name: "Priya M.",
    loc: "Tirupati",
    text: "Years of wrist pain from carpal tunnel, and within days of Dr. Suneel's surgery I felt complete relief. Explained everything clearly. Highly recommended.",
    stars: 5,
    initials: "PM",
    tag: "Carpal Tunnel",
    category: "Nerve",
  },
  {
    name: "Venkat S.",
    loc: "Secunderabad",
    text: "Brachial plexus injury from a road accident. Dr. Suneel's microsurgical expertise restored almost complete arm movement. A life-changing outcome.",
    stars: 5,
    initials: "VS",
    tag: "Brachial Plexus",
    category: "Brachial Plexus",
  },
  {
    name: "Lakshmi R.",
    loc: "Hyderabad",
    text: "My child had a congenital hand deformity. Post surgery, the improvement is remarkable. Dr. Suneel's compassion and skill made all the difference.",
    stars: 5,
    initials: "LR",
    tag: "Congenital Deformity",
    category: "Hand",
  },
  {
    name: "Arjun D.",
    loc: "Warangal",
    text: "My scaphoid fracture wasn't healing for months. Dr. Suneel's vascularised bone grafting procedure finally gave me my wrist function back. Truly grateful.",
    stars: 5,
    initials: "AD",
    tag: "Scaphoid Non-Union",
    category: "Wrist",
  },
  {
    name: "Sunita P.",
    loc: "Nizamabad",
    text: "Cubital tunnel syndrome was affecting my work as a tailor. After surgery I have no more numbness and my grip strength is completely back.",
    stars: 5,
    initials: "SP",
    tag: "Cubital Tunnel",
    category: "Nerve",
  },
];

const FILTERS = ["All", "Hand", "Wrist", "Nerve", "Brachial Plexus"];

const STATS = [
  { number: "4.9★", label: "Average Rating" },
  { number: "10000+", label: "Happy Patients" },
  { number: "98%", label: "Success Rate" },
  { number: "15+", label: "Years of Trust" },
];

function Stars({ count }) {
  return (
    <div className="tp-stars" aria-label={`${count} out of 5 stars`}>
      {[...Array(count)].map((_, i) => (
        <span key={i}>★</span>
      ))}
    </div>
  );
}

export default function TestimonialsPage() {
  const [filter, setFilter] = useState("All");

  const featured = useMemo(() => TESTIMONIALS.find((t) => t.featured), []);
  const visible = useMemo(() => {
    const rest = TESTIMONIALS.filter((t) => !t.featured);
    if (filter === "All") return rest;
    return rest.filter((t) => t.category === filter);
  }, [filter]);

  return (
    <div className="tp-page">

      <SeoHead
        title="Patient Testimonials — Hand & Wrist Clinics"
        description="Real patient stories and outcomes following hand, wrist and nerve surgery by Dr. R. Suneel in Hyderabad."
        keywords="hand surgery testimonials Hyderabad, hand surgeon reviews Hyderabad, wrist surgery testimonials Hyderabad, patient reviews Dr R Suneel, hand and wrist clinic reviews, hand surgery success stories, wrist surgery patient feedback, peripheral nerve surgery testimonials, brachial plexus surgery reviews, best hand surgeon Hyderabad reviews, Medicover hand surgery testimonials, Dr R Suneel patient testimonials"
        url="https://www.handandwristclinics.com/testimonials"
      />

      {/* HERO + BREADCRUMB */}
      <header className="tp-hero">
        <div className="tp-hero-inner">
          <nav className="tp-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span className="tp-bc-sep">/</span>
            <span className="tp-bc-current">Testimonials</span>
          </nav>
          <div className="tp-eyebrow">
            <span></span> Patient Stories
          </div>
          <h1 className="tp-title">
            Real Outcomes, <em>Real Trust</em>
          </h1>
          <div className="tp-divider" />
          <p className="tp-hero-sub">
            Genuine experiences from patients who regained function, mobility,
            and confidence under Dr. Suneel's care.
          </p>
        </div>
      </header>

      {/* STATS STRIP */}
      <section className="tp-stats">
        <div className="tp-container">
          <div className="tp-stats-grid">
            {STATS.map((s, i) => (
              <div className="tp-stat" key={i}>
                <div className="tp-stat-num">{s.number}</div>
                <div className="tp-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED TESTIMONIAL */}
      {featured && (
        <section className="tp-section tp-featured-section">
          <div className="tp-container">
            <div className="tp-featured">
              <div className="tp-featured-badge">Featured Story</div>
              <div className="tp-featured-mark">"</div>
              <p className="tp-featured-text">{featured.text}</p>
              <div className="tp-featured-foot">
                <div className="tp-featured-avatar">{featured.initials}</div>
                <div className="tp-featured-meta">
                  <div className="tp-featured-name">{featured.name}</div>
                  <div className="tp-featured-loc">{featured.loc}</div>
                </div>
                <div className="tp-featured-right">
                  <Stars count={featured.stars} />
                  <div className="tp-featured-tag">{featured.tag}</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FILTER + GRID */}
      <section className="tp-section tp-grid-section">
        <div className="tp-container">
          <div className="tp-section-head">
            <div className="tp-section-tag">More Stories</div>
            <h2 className="tp-section-title">
              Recovery stories <em>across specialties</em>
            </h2>
            <div className="tp-divider" />
          </div>

          <div className="tp-filters" role="tablist">
            {FILTERS.map((f) => (
              <button
                key={f}
                className={`tp-filter ${filter === f ? "active" : ""}`}
                onClick={() => setFilter(f)}
                role="tab"
                aria-selected={filter === f}
              >
                {f}
                {f !== "All" && (
                  <span className="tp-filter-count">
                    {TESTIMONIALS.filter((t) => !t.featured && t.category === f).length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {visible.length > 0 ? (
            <div className="tp-grid">
              {visible.map((t, i) => (
                <article className="tp-card" key={`${filter}-${i}`}>
                  <div className="tp-card-mark">"</div>
                  <div className="tp-card-top">
                    <div className="tp-card-avatar">{t.initials}</div>
                    <div className="tp-card-top-right">
                      <Stars count={t.stars} />
                      <div className="tp-card-tag">{t.tag}</div>
                    </div>
                  </div>
                  <p className="tp-card-text">{t.text}</p>
                  <div className="tp-card-foot">
                    <div className="tp-card-name">{t.name}</div>
                    <div className="tp-card-loc">{t.loc}</div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="tp-empty">
              <p>No testimonials in this category yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="tp-cta-section">
        <div className="tp-container tp-cta-inner">
          <h2 className="tp-cta-title">
            Share Your <em>Experience</em>
          </h2>
          <p className="tp-cta-text">
            Your recovery story can inspire others facing similar challenges.
            We'd love to hear from you.
          </p>
          <div className="tp-cta-buttons">
            <Link to="/contact" className="tp-cta-btn tp-cta-btn-primary">
              Write a Review
              <span className="tp-cta-arrow">→</span>
            </Link>
            <Link to="/contact" className="tp-cta-btn tp-cta-btn-outline">
              Book a Consultation
            </Link>
          </div>
        </div>
      </section>

     
    </div>
  );
}